import { useEffect, useState, RefObject } from 'react';
import { useDebug } from "@/utils/useDebugLog";

interface UseCustomCursorOptions {
  containerRef: RefObject<HTMLElement>;
  isDisabled: boolean;
}

interface CursorPosition {
  x: number;
  y: number;
}

export const useCustomCursor = ({
  containerRef,
  isDisabled
}: UseCustomCursorOptions) => {
  const { log } = useDebug({ name: 'CustomCursor', bgColor: '#042940', textColor: 'white', enabled: false });

  const [cursorPos, setCursorPos] = useState<CursorPosition>({ x: -100, y: -100 }); // Start offscreen
  const [showCursor, setShowCursor] = useState(false);

  // Track cursor position over container
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setCursorPos({ x: e.clientX, y: e.clientY });
    };

    const handleMouseEnter = (e: MouseEvent) => {
      // log('Mouse entered game board');
      setCursorPos({ x: e.clientX, y: e.clientY });
      if (!isDisabled) {
        setShowCursor(true);
      }
    };

    const handleMouseLeave = () => {
      // log('Mouse left game board');
      setShowCursor(false);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseenter', handleMouseEnter);
      container.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseenter', handleMouseEnter);
        container.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [containerRef, isDisabled, log]);

  // Hide cursor when disabled state changes
  useEffect(() => {
    if (isDisabled) {
      setShowCursor(false);
    }
  }, [isDisabled]);

  return { cursorPos, showCursor };
};
